/**
 * Cart Drawer Gift Selector
 * Handles color selection for bonus gift socks
 */

class CartDrawerGiftSelector {
  constructor() {
    this.modal = document.getElementById('color-modal');
    this.modalContent = this.modal?.querySelector('.cart-drawer__color-modal-content');
    this.modalOverlay = this.modal?.querySelector('.cart-drawer__color-modal-overlay');
    this.closeBtn = this.modal?.querySelector('.cart-drawer__color-modal-close');
    this.addBtn = this.modal?.querySelector('.cart-drawer__add-gift-btn');
    this.colorOptions = this.modal?.querySelectorAll('.cart-drawer__color-option');

    this.currentTier = null;
    this.selectedColor = null;
    this.selectedVariantId = null;
    this.buttonsBound = false;

    /**
     * TIER CONFIGURATION
     *
     * To add a new tier:
     * 1. Add a new tier object with a unique number key (e.g., '3', '4')
     * 2. Configure the tier settings below
     *
     * To remove a tier:
     * 1. Simply delete or comment out the tier object
     *
     * Tier Properties:
     * - handle: Product handle in Shopify
     * - requiresModal: true = opens modal for selection, false = adds directly to cart
     * - variantId: (for direct add) Single variant ID to add
     * - variants: (for modal) Object mapping variant options to IDs
     * - buttonText.default: Text shown on button before gift is added
     * - buttonText.added: Text shown after gift is in cart
     * - detection.discountCode: Discount code name to detect this gift
     * - detection.productKeyword: Keyword in product title to identify this product
     */
    this.tierConfig = {
      '1': {
        handle: 'free-gift-socks',
        requiresModal: true, // Set to true if tier needs color/variant selection modal
        variants: {
          'black': '51290015433016',
          'white': '51290015400248'
        },
        buttonText: {
          default: 'Select Colour',
          added: 'Added ✓'
        },
        // Detection settings for checking if gift is in cart
        detection: {
          discountCode: 'FREE SOCKS', // Discount code name to check
          productKeyword: 'sock' // Keyword in product title to identify this product
        }
      },
      // TIER 2 EXAMPLE - Uncomment to enable
      // '2': {
      //   handle: 'creatine-monohydrate',
      //   requiresModal: false, // No modal needed, direct add to cart
      //   variantId: '51284335952184', // Creatine Monohydrate 200g
      //   buttonText: {
      //     default: 'Add to Cart',
      //     added: 'Added ✓'
      //   },
      //   // Detection settings for checking if gift is in cart
      //   detection: {
      //     discountCode: 'FREE CREATINE',
      //     productKeyword: 'creatine'
      //   }
      // }

      // ADD MORE TIERS HERE
      // '3': { ... }
    };

    this.init();
  }

  init() {
    // Bind event listeners
    this.bindSelectButtons();

    // Only bind modal-related events if modal exists
    if (this.modal) {
      this.bindColorOptions();
      this.bindCloseButtons();
      this.bindAddButton();
    }

    // Check if gifts are already in cart
    this.checkExistingGifts();

    // Listen for cart updates
    this.observeCartChanges();
  }

  async checkExistingGifts() {
    try {
      const cartData = await fetch('/cart.js').then(r => r.json());

      // console.log('Checking existing gifts, cart items:', cartData.items.length);

      // First, reset all buttons to default state
      const allSelectButtons = document.querySelectorAll('.cart-drawer__select-color-btn');
      // console.log('Found select buttons to reset:', allSelectButtons.length);

      allSelectButtons.forEach(btn => {
        const tier = btn.dataset.tier;
        const tierConfig = this.tierConfig[tier];

        // Set button text based on tier config
        if (tierConfig && tierConfig.buttonText) {
          btn.textContent = tierConfig.buttonText.default;
        }
        btn.disabled = false;
        btn.classList.remove('cart-drawer__select-color-btn--added');
        // console.log('Reset button for tier:', btn.dataset.tier);
      });

      // Then check each cart item for gift properties and update accordingly
      const giftsInCart = [];

      // Build discount code checks dynamically from tierConfig
      const tierDiscounts = {};
      Object.keys(this.tierConfig).forEach(tier => {
        const config = this.tierConfig[tier];
        if (config.detection && config.detection.discountCode) {
          tierDiscounts[tier] = cartData.cart_level_discount_applications?.some(
            discount => discount.title?.toUpperCase().includes(config.detection.discountCode.toUpperCase())
          ) || false;
        }
      });

      cartData.items.forEach(item => {
        // Check if item is marked as free gift OR has a discount making it free
        const isFreeGift = item.properties && item.properties._is_free_gift === 'true';
        const isFreePrice = item.final_line_price === 0 || item.original_line_price === 0;

        // Check each tier dynamically
        Object.keys(this.tierConfig).forEach(tier => {
          const config = this.tierConfig[tier];
          let isTierGift = false;

          // Check if this item is a free gift for this tier
          if (isFreeGift && item.properties._gift_tier === tier) {
            isTierGift = true;
          }

          // Check if discount code is applied for this tier
          if (config.detection && tierDiscounts[tier]) {
            const keyword = config.detection.productKeyword.toLowerCase();
            if (item.product_title?.toLowerCase().includes(keyword) && isFreePrice) {
              isTierGift = true;
            }
          }

          // Update button if this is a tier gift
          if (isTierGift) {
            giftsInCart.push(tier);
            const rewardItem = document.querySelector(`[data-reward-tier="${tier}"]`);
            const selectBtn = rewardItem?.querySelector('.cart-drawer__select-color-btn');
            if (selectBtn && config.buttonText) {
              selectBtn.textContent = config.buttonText.added;
              selectBtn.disabled = true;
              selectBtn.classList.add('cart-drawer__select-color-btn--added');
            }
          }
        });
      });

      // console.log('Gifts found in cart:', giftsInCart.length > 0 ? giftsInCart : 'none');
    } catch (error) {
      console.warn('Could not check existing gifts:', error);
    }
  }

  async checkTier2Status(cartData) {
    const tier2Threshold = 15000; // $150.00 in cents
    const tier2Config = this.tierConfig['2'];

    // Calculate cart total EXCLUDING free gifts to prevent circular logic
    let cartTotalExcludingGifts = 0;
    let hasFreeCreatine = false;

    cartData.items.forEach(item => {
      const isFreeGift = item.properties && item.properties._is_free_gift === 'true';

      // Only count items that are NOT free gifts
      if (!isFreeGift) {
        cartTotalExcludingGifts += item.final_line_price;
      }

      // Check if free tier 2 Creatine gift is already in cart
      if (item.variant_id.toString() === tier2Config.variantId &&
          isFreeGift &&
          item.properties._gift_tier === '2') {
        hasFreeCreatine = true;
      }
    });

    console.log('Cart total (excluding gifts):', cartTotalExcludingGifts, 'Threshold:', tier2Threshold);
    console.log('Has free Creatine gift:', hasFreeCreatine);

    // Check if tier 2 is unlocked and show popup if configured
    if (cartTotalExcludingGifts >= tier2Threshold && !hasFreeCreatine) {
      // Show popup notification if enabled and not already shown for this session
      if (tier2Config.showPopup && !sessionStorage.getItem('tier2_popup_shown')) {
        this.showTier2Popup();
        sessionStorage.setItem('tier2_popup_shown', 'true');
      }

      // Auto-add if enabled (legacy support)
      if (tier2Config.autoAdd) {
        console.log('Tier 2 unlocked! Auto-adding free Creatine Monohydrate...');
        await this.autoAddCreatine();
      }
    }

    // Clear popup flag if cart drops below threshold
    if (cartTotalExcludingGifts < tier2Threshold) {
      sessionStorage.removeItem('tier2_popup_shown');
    }
  }

  showTier2Popup() {
    // Create popup notification element
    const popup = document.createElement('div');
    popup.className = 'cart-drawer__tier-popup';
    popup.innerHTML = `
      <div class="cart-drawer__tier-popup-content">
        <svg class="cart-drawer__tier-popup-icon" width="48" height="48" viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="10" fill="currentColor"/>
          <path d="M6 10L9 13L14 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <h3 class="cart-drawer__tier-popup-title">🎉 Congratulations!</h3>
        <p class="cart-drawer__tier-popup-message">You've unlocked <strong>Free 200g Creatine Monohydrate</strong>!</p>
        <p class="cart-drawer__tier-popup-instruction">Click the "Add to Cart" button in your cart to claim it.</p>
        <button type="button" class="cart-drawer__tier-popup-close">Got it!</button>
      </div>
    `;

    document.body.appendChild(popup);

    // Trigger animation
    setTimeout(() => {
      popup.classList.add('cart-drawer__tier-popup--visible');
    }, 10);

    // Close button handler
    const closeBtn = popup.querySelector('.cart-drawer__tier-popup-close');
    closeBtn?.addEventListener('click', () => {
      popup.classList.remove('cart-drawer__tier-popup--visible');
      setTimeout(() => {
        popup.remove();
      }, 300);
    });

    // Auto-close after 8 seconds
    setTimeout(() => {
      if (popup.parentNode) {
        popup.classList.remove('cart-drawer__tier-popup--visible');
        setTimeout(() => {
          popup.remove();
        }, 300);
      }
    }, 8000);
  }

  async autoAddCreatine() {
    const tier2Config = this.tierConfig['2'];

    if (!tier2Config.variantId) {
      console.warn('Tier 2 variant ID not configured');
      return;
    }

    try {
      const formData = {
        items: [{
          id: tier2Config.variantId,
          quantity: 1,
          properties: {
            '_gift_tier': '2',
            '_is_free_gift': 'true'
          }
        }]
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        console.log('Creatine Monohydrate added successfully!');

        // Update the cart drawer to show the new item
        await this.updateCartDrawer();
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('Failed to auto-add creatine (422 error). Possible issues:');
        console.error('1. Variant ID may be incorrect:', tier2Config.variantId);
        console.error('2. Product may not be available or published');
        console.error('3. Variant may be out of stock');
        console.error('Full error details:', errorData);

        // Show a more helpful message
        if (errorData.description) {
          console.error('Shopify says:', errorData.description);
        }
      }
    } catch (error) {
      console.error('Error auto-adding creatine:', error);
    }
  }

  observeCartChanges() {
    // Listen for cart drawer mutations to detect when cart items change
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    let debounceTimer;
    const observer = new MutationObserver(() => {
      // Debounce to avoid running multiple times during rapid DOM changes
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        this.checkExistingGifts();
      }, 100);
    });

    // Observe the cart items container for changes
    const cartItemsContainer = document.getElementById('CartDrawer-CartItems');
    if (cartItemsContainer) {
      observer.observe(cartItemsContainer, {
        childList: true,
        subtree: true
      });
    }

    // Also listen for custom cart events with debounce
    document.addEventListener('cart:updated', () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        this.checkExistingGifts();
      }, 100);
    });

    document.addEventListener('cart:refresh', () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        this.checkExistingGifts();
      }, 100);
    });
  }

  bindSelectButtons() {
    // Only bind once to prevent multiple event listeners
    if (this.buttonsBound) return;
    this.buttonsBound = true;

    // Use event delegation on document body to handle dynamically updated buttons
    // This ensures it works even when the rewards section is replaced
    document.body.addEventListener('click', (e) => {
      const btn = e.target.closest('.cart-drawer__select-color-btn');
      if (btn && !btn.disabled && !btn.classList.contains('cart-drawer__select-color-btn--added')) {
        e.preventDefault();
        e.stopPropagation();

        this.currentTier = btn.dataset.tier;
        const tierConfig = this.tierConfig[this.currentTier];

        if (!tierConfig) {
          console.warn('No configuration found for tier:', this.currentTier);
          return;
        }

        // Handle based on tier config
        if (tierConfig.requiresModal) {
          // Open color/variant selection modal
          this.openModal();
        } else {
          // Direct add to cart (no modal)
          this.addGiftDirectly(btn, tierConfig);
        }
      }
    });
  }

  bindColorOptions() {
    this.colorOptions?.forEach(option => {
      option.addEventListener('click', (e) => {
        const btn = e.currentTarget;
        this.selectColor(btn);
      });
    });
  }

  bindCloseButtons() {
    this.closeBtn?.addEventListener('click', () => this.closeModal());
    this.modalOverlay?.addEventListener('click', () => this.closeModal());
  }

  bindAddButton() {
    this.addBtn?.addEventListener('click', () => this.addGiftToCart());
  }

  openModal() {
    // console.log('Opening modal, tier:', this.currentTier);

    // Re-query modal in case it was replaced during cart update
    this.modal = document.getElementById('color-modal');

    if (!this.modal) {
      console.error('Modal not found in DOM!');
      return;
    }

    // console.log('Modal found, displaying...');

    // Re-query modal child elements in case they were replaced
    this.modalContent = this.modal.querySelector('.cart-drawer__color-modal-content');
    this.modalOverlay = this.modal.querySelector('.cart-drawer__color-modal-overlay');
    this.closeBtn = this.modal.querySelector('.cart-drawer__color-modal-close');
    this.addBtn = this.modal.querySelector('.cart-drawer__add-gift-btn');
    this.colorOptions = this.modal.querySelectorAll('.cart-drawer__color-option');

    // Rebind modal-specific events
    this.bindCloseButtons();
    this.bindColorOptions();
    this.bindAddButton();

    this.modal.style.display = 'block';
    // Force reflow
    this.modal.offsetHeight;
    this.modal.classList.add('cart-drawer__color-modal--active');

    // Reset selection
    this.resetSelection();

    // Update variant IDs based on tier
    this.updateVariantIds();

    // console.log('Modal opened successfully');
  }

  closeModal() {
    if (!this.modal) return;

    this.modal.classList.remove('cart-drawer__color-modal--active');
    setTimeout(() => {
      this.modal.style.display = 'none';
    }, 300);
  }

  resetSelection() {
    this.selectedColor = null;
    this.selectedVariantId = null;
    this.colorOptions?.forEach(opt => opt.classList.remove('cart-drawer__color-option--selected'));
    if (this.addBtn) {
      this.addBtn.disabled = true;
    }
  }

  updateVariantIds() {
    if (!this.currentTier || !this.tierConfig[this.currentTier]) return;

    const tierVariants = this.tierConfig[this.currentTier].variants;
    this.colorOptions?.forEach(option => {
      const color = option.dataset.color;
      if (tierVariants[color]) {
        option.dataset.variantId = tierVariants[color];
      }
    });
  }

  selectColor(button) {
    // Remove previous selection
    this.colorOptions?.forEach(opt => opt.classList.remove('cart-drawer__color-option--selected'));

    // Add selection to clicked button
    button.classList.add('cart-drawer__color-option--selected');

    // Store selection
    this.selectedColor = button.dataset.color;
    this.selectedVariantId = button.dataset.variantId;

    // Enable add button
    if (this.addBtn) {
      this.addBtn.disabled = false;
    }
  }

  async addGiftToCart() {
    if (!this.selectedVariantId || !this.currentTier) {
      console.warn('Missing variant ID or tier');
      return;
    }

    // Check if variant ID is placeholder
    if (this.selectedVariantId === '0') {
      alert('Please configure the product variant IDs in cart-drawer-gift-selector.js first. The variant ID is currently set to "0".');
      return;
    }

    // Disable button during request
    if (this.addBtn) {
      this.addBtn.disabled = true;
      this.addBtn.textContent = 'Adding...';
    }

    try {
      const formData = {
        items: [{
          id: this.selectedVariantId,
          quantity: 1,
          properties: {
            '_gift_tier': this.currentTier,
            '_gift_color': this.selectedColor,
            '_is_free_gift': 'true'
          }
        }]
      };

      // console.log('Adding gift to cart:', formData);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const result = await response.json();
        // console.log('Gift added successfully:', result);

        // Update button UI immediately
        const rewardItem = document.querySelector(`[data-reward-tier="${this.currentTier}"]`);
        const selectBtn = rewardItem?.querySelector('.cart-drawer__select-color-btn');
        if (selectBtn) {
          selectBtn.textContent = 'Added';
          selectBtn.disabled = true;
          selectBtn.classList.add('cart-drawer__select-color-btn--added');
        }

        // Close modal
        this.closeModal();

        // Update cart drawer in real-time
        await this.updateCartDrawer();
      } else {
        // Log detailed error information
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('Cart API Error:', errorData);
        throw new Error(errorData.description || errorData.message || 'Failed to add gift to cart');
      }
    } catch (error) {
      console.error('Error adding gift:', error);
      alert('Sorry, there was an error adding the gift to your cart. Please try again.');
    } finally {
      // Reset button
      if (this.addBtn) {
        this.addBtn.disabled = false;
        this.addBtn.textContent = 'Add to Cart';
      }
    }
  }

  async addGiftDirectly(button, tierConfig) {
    if (!tierConfig.variantId) {
      console.warn('Variant ID not configured for tier');
      alert('Gift configuration error. Please contact support.');
      return;
    }

    // Check if variant ID is placeholder
    if (tierConfig.variantId === '0') {
      alert('Please configure the product variant IDs in cart-drawer-gift-selector.js first. The variant ID is currently set to "0".');
      return;
    }

    // Disable button during request
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      const formData = {
        items: [{
          id: tierConfig.variantId,
          quantity: 1,
          properties: {
            '_gift_tier': this.currentTier,
            '_is_free_gift': 'true'
          }
        }]
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        console.log(`Tier ${this.currentTier} gift added successfully!`);

        // Update button UI
        button.textContent = tierConfig.buttonText?.added || 'Added ✓';
        button.classList.add('cart-drawer__select-color-btn--added');

        // Update cart drawer in real-time
        await this.updateCartDrawer();
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('Failed to add gift:', errorData);
        throw new Error(errorData.description || errorData.message || 'Failed to add gift to cart');
      }
    } catch (error) {
      console.error('Error adding gift:', error);
      alert('Sorry, there was an error adding the gift to your cart. Please try again.');

      // Reset button on error
      button.textContent = originalText;
      button.disabled = false;
    }
  }

  async updateCartDrawer() {
    try {
      // Fetch updated cart data
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();

      // console.log('Updating cart drawer with:', cartData);

      // Update cart count in header
      this.updateCartCount(cartData.item_count);

      // Fetch cart drawer section HTML
      const sectionsResponse = await fetch('/?sections=cart-drawer');
      const sectionsData = await sectionsResponse.json();

      if (sectionsData['cart-drawer']) {
        // Parse the HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionsData['cart-drawer'], 'text/html');

        // Update cart items
        const newCartItems = doc.querySelector('#CartDrawer-CartItems');
        const currentCartItems = document.querySelector('#CartDrawer-CartItems');
        if (newCartItems && currentCartItems) {
          currentCartItems.innerHTML = newCartItems.innerHTML;
        }

        // Update cart footer (totals, etc)
        const newCartFooter = doc.querySelector('.drawer__footer');
        const currentCartFooter = document.querySelector('.drawer__footer');
        if (newCartFooter && currentCartFooter) {
          currentCartFooter.innerHTML = newCartFooter.innerHTML;
        }

        // Update incentives section (progress bars, etc)
        const newIncentives = doc.querySelector('.cart-drawer__incentives');
        const currentIncentives = document.querySelector('.cart-drawer__incentives');
        if (newIncentives && currentIncentives) {
          currentIncentives.innerHTML = newIncentives.innerHTML;
        }

        // console.log('Cart drawer updated successfully');

        // IMPORTANT: Always check existing gifts after ALL DOM updates
        // Use setTimeout to ensure DOM has fully rendered
        setTimeout(() => {
          this.checkExistingGifts();
        }, 50);

        // Dispatch event for other scripts that might need to know
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartData }));
      }
    } catch (error) {
      console.error('Failed to update cart drawer:', error);
      // If update fails, show a message
      alert('Gift added successfully! Please close and reopen the cart to see the update.');
    }
  }

  updateCartCount(itemCount) {
    // Update cart icon bubble
    const cartCountBubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span');
    if (cartCountBubble) {
      cartCountBubble.textContent = itemCount;
    }

    // Show/hide cart count bubble
    const cartCountBubbleContainer = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (cartCountBubbleContainer) {
      if (itemCount > 0) {
        cartCountBubbleContainer.style.display = '';
      } else {
        cartCountBubbleContainer.style.display = 'none';
      }
    }
  }

}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartDrawerGiftSelector();
  });
} else {
  new CartDrawerGiftSelector();
}
